import { type ImgHTMLAttributes, type ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';

const PLACEHOLDER_LOGO = '/images/logo-header.png';
/** Below this box width the logo shrinks to an unreadable smudge, so the placeholder stays a plain tile. */
const MIN_LOGO_BOX_WIDTH = 64;

/**
 * Sitewide stand-in for a missing or unloadable image: a light-grey tile with the faded JABLOTRON
 * logo, sized by `className` — pass the same size classes the real image would have had, so the
 * layout doesn't shift. The logo scales with that box (70% of its width, capped at 180px) instead of
 * each caller picking its own logo size, and is left out of boxes narrower than 64px.
 */
export function ImagePlaceholder({ className = '', label }: { className?: string; label?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [showLogo, setShowLogo] = useState(false);

  // Measured rather than a container query: some callers size the box by height or max-width only,
  // and inline-size containment would collapse those to zero width.
  useLayoutEffect(() => {
    const box = ref.current;
    if (!box) {
      return undefined;
    }
    const update = () => setShowLogo(box.offsetWidth >= MIN_LOGO_BOX_WIDTH);
    update();
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(update);
    observer.observe(box);

    return () => observer.disconnect();
  }, []);

  return (
    <span
      ref={ref}
      data-testid="image-placeholder"
      className={`flex items-center justify-center rounded-lg bg-brand-surface ${className}`}
      {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
    >
      {showLogo && <img src={PLACEHOLDER_LOGO} alt="" className="w-[70%] max-w-[180px] opacity-25" />}
    </span>
  );
}

type FallbackImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'onError'> & {
  src?: string;
  /** Rendered instead of the image when `src` is missing or fails to load. Defaults to `<ImagePlaceholder>` with the image's own `className`. */
  fallback?: ReactNode;
};

/**
 * `<img>` that swaps itself for a placeholder when `src` is empty or the request fails (e.g. the
 * e-shop image API answering 401) — otherwise the browser paints the alt text in its place, which
 * overflows small product thumbnails.
 */
export function FallbackImage({ src, alt, className, fallback, ...imgProps }: FallbackImageProps) {
  // Remember *which* src failed, so switching to another src (e.g. a different variant) retries.
  const [failedSrc, setFailedSrc] = useState<string>();
  const imgRef = useRef<HTMLImageElement>(null);

  // An image can fail before React's error listener is attached (e.g. an error response the browser
  // already has cached), and then onError never fires; a finished load with no pixels is a failure too.
  useEffect(() => {
    const img = imgRef.current;
    if (img && src && img.complete && img.naturalWidth === 0) {
      setFailedSrc(src);
    }
  }, [src]);

  if (!src || failedSrc === src) {
    return <>{fallback ?? <ImagePlaceholder className={className} label={alt} />}</>;
  }

  return <img {...imgProps} ref={imgRef} src={src} alt={alt} className={className} onError={() => setFailedSrc(src)} />;
}
